import type { ApiError } from './types';

/**
 * Convierte cualquier error de la capa HTTP a `ApiError`.
 *  - `body` es el payload deserializado de un response no-2xx (cuando
 *    openapi-fetch pudo parsearlo) o el `Error` capturado en un fallo
 *    de red/abort/timeout.
 *  - `response` esta presente cuando el servidor respondio (no-2xx).
 *    Ausente si la conexion fallo antes de obtener respuesta.
 */
export function normalizeError(body: unknown, response?: Response): ApiError {
  if (response === undefined) {
    if (body instanceof Error) {
      if (body.name === 'AbortError') {
        return {
          statusCode: 0,
          code: 'CANCELED',
          message: ['Solicitud cancelada.'],
        };
      }
      const msg = (body.message ?? '').toLowerCase();
      if (msg.includes('timeout') || msg.includes('timed out')) {
        return {
          statusCode: 0,
          code: 'TIMEOUT',
          message: ['La solicitud tardó demasiado. Verifique su conexión e intente nuevamente.'],
        };
      }
      return {
        statusCode: 0,
        code: 'NETWORK_ERROR',
        message: ['No se pudo contactar al servidor. Verifique su conexión.'],
        details: body,
      };
    }
    return {
      statusCode: 0,
      code: 'UNKNOWN_ERROR',
      message: ['Ocurrió un error inesperado.'],
      details: body,
    };
  }

  const status = response.status;
  const parsed = body as
    | {
        code?: string;
        message?: string | string[];
        errors?: Record<string, string>;
      }
    | undefined;

  const retryAfterSeconds = extractRetryAfterSeconds(response, parsed);

  return {
    statusCode: status,
    code: parsed?.code ?? defaultCodeForStatus(status),
    message: normalizeMessages(parsed?.message, status),
    ...(parsed?.errors ? { fieldErrors: parsed.errors } : {}),
    ...(retryAfterSeconds !== undefined ? { retryAfterSeconds } : {}),
    details: parsed,
  };
}

/**
 * Calcula los segundos a esperar antes de reintentar (rate-limit/lockout).
 * Prioridad:
 *  1. Cuerpo: `metadata.secondsRemaining` o `secondsRemaining` plano (valor de
 *     negocio más preciso, p.ej. lockout de cuenta con `metadata`).
 *  2. Header `Retry-After`: entero de segundos o fecha HTTP (lo emite el
 *     ThrottlerGuard de NestJS en los 429).
 * Devuelve `undefined` si no hay ninguna señal.
 */
function extractRetryAfterSeconds(response: Response, parsed: unknown): number | undefined {
  const fromBody = readSecondsFromBody(parsed);
  if (fromBody !== undefined) return fromBody;

  const header = response.headers.get('Retry-After');
  if (header === null) return undefined;

  const asNumber = Number(header);
  if (Number.isFinite(asNumber)) return Math.max(0, Math.ceil(asNumber));

  const asDate = Date.parse(header);
  if (!Number.isNaN(asDate)) return Math.max(0, Math.ceil((asDate - Date.now()) / 1000));

  return undefined;
}

function readSecondsFromBody(parsed: unknown): number | undefined {
  if (parsed === null || typeof parsed !== 'object') return undefined;

  const metadata = (parsed as { metadata?: unknown }).metadata;
  if (metadata !== null && typeof metadata === 'object') {
    const nested = (metadata as { secondsRemaining?: unknown }).secondsRemaining;
    if (typeof nested === 'number' && Number.isFinite(nested)) return nested;
  }

  const flat = (parsed as { secondsRemaining?: unknown }).secondsRemaining;
  return typeof flat === 'number' && Number.isFinite(flat) ? flat : undefined;
}

function normalizeMessages(raw: string | string[] | undefined, status: number): string[] {
  if (Array.isArray(raw)) return raw.length > 0 ? raw : [defaultMessageForStatus(status)];
  if (typeof raw === 'string' && raw.trim().length > 0) return [raw];
  return [defaultMessageForStatus(status)];
}

function defaultCodeForStatus(status: number): string {
  if (status === 400) return 'VALIDATION_ERROR';
  if (status === 401) return 'UNAUTHORIZED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 409) return 'CONFLICT';
  if (status === 422) return 'UNPROCESSABLE';
  if (status === 429) return 'RATE_LIMITED';
  if (status >= 500) return 'SERVER_ERROR';
  return 'HTTP_ERROR';
}

function defaultMessageForStatus(status: number): string {
  if (status === 400) return 'La solicitud contiene datos inválidos.';
  if (status === 401) return 'Su sesión expiró o no tiene autorización.';
  if (status === 403) return 'No tiene permisos para realizar esta acción.';
  if (status === 404) return 'El recurso solicitado no existe.';
  if (status === 409) return 'Conflicto con el estado actual del recurso.';
  if (status === 422) return 'No se pudo procesar la solicitud.';
  if (status === 429) return 'Demasiadas solicitudes. Espere un momento.';
  if (status >= 500) return 'Error en el servidor. Intente más tarde.';
  return 'Error en la solicitud.';
}
